/**
 * Dữ liệu mẫu của Runtime License API cục bộ (ADR-0058 mục 6.1). Chỉ có dữ
 * liệu; màu và nhãn ở `modules/license-cuc-bo/constants.ts`.
 *
 * Xem từng trạng thái license bằng `?license=EXPIRING|GRACE|READ_ONLY|NOT_YET_VALID|INVALID`
 * (mặc định `ACTIVE`), kết hợp được với `?mock=loading|error`. `?mock=empty`
 * là bản cài chưa nhập license nào.
 */
import type { LicenseCucBo, TrangThaiCucBo } from '../modules/license-cuc-bo/nghiep-vu/types';

export const MOCK_INSTALLATION_ID = '0f8fad5b-d9cb-469f-a165-70867728950e';
export const MOCK_TENANT_ID = '7c9e6679-7425-40de-944b-e07fc1f90ae7';

const NGAY = 24 * 60 * 60 * 1000;

function iso(ms: number): string {
  return new Date(Math.floor(ms / 1000) * 1000).toISOString().replace('.000Z', 'Z');
}

/** Số ngày còn lại tới mốc kế, theo trạng thái mẫu. */
const SO_NGAY: Record<TrangThaiCucBo, number> = {
  ACTIVE: 186,
  EXPIRING: 21,
  GRACE: 4,
  READ_ONLY: 0,
  NOT_YET_VALID: 3,
  INVALID: 0,
};

export function trangThaiMau(status: TrangThaiCucBo): LicenseCucBo {
  const bayGio = Date.now();
  let het = bayGio + SO_NGAY.ACTIVE * NGAY;
  if (status === 'EXPIRING') het = bayGio + SO_NGAY.EXPIRING * NGAY;
  if (status === 'GRACE') het = bayGio - 3 * NGAY;
  if (status === 'READ_ONLY') het = bayGio - 30 * NGAY;
  const tu = status === 'NOT_YET_VALID' ? bayGio + SO_NGAY.NOT_YET_VALID * NGAY : het - 365 * NGAY;
  return {
    status,
    error_code: status === 'INVALID' ? 'LICENSE_FORMAT_INVALID' : null,
    clock_warning: false,
    effective_time: iso(bayGio),
    days_left: SO_NGAY[status],
    license:
      status === 'INVALID'
        ? null
        : {
            jti: '5b0f3c1e-8a42-4c55-9a1d-2f7b8e6c4d10',
            digest: 'a0dbc3b0e0f6e094bf3f2bbf3bbf2ebcadc52f3e00fe74230bbf09f95410b14b',
            kid: 'lic-2026-01',
            tenant_id: MOCK_TENANT_ID,
            installation_id: MOCK_INSTALLATION_ID,
            product_code: 'platform-enterprise',
            license_schema: '1.1',
            plan: 'ENTERPRISE',
            issued_at: iso(tu - NGAY),
            not_before: iso(tu),
            expires_at: iso(het),
            grace_until: iso(het + 7 * NGAY),
            entitlements: {
              modules: ['core', 'crm', 'hrm', 'erp'],
              max_users: 300,
              max_nodes: 3,
              limits: { storage_gb: 200, bots: 20 },
              features: ['sso_enterprise_idp'],
            },
          },
    keyring_sequence: 2,
    revocation_sequence: status === 'INVALID' ? null : 1,
    installation_id: MOCK_INSTALLATION_ID,
    usage: { max_users: 214, max_nodes: 3, bots: 17 },
  };
}

/** `activation-request.json` mẫu, đúng dạng bộ cài sinh (chuẩn hoá JCS, một dòng). */
export const MOCK_ACTIVATION_REQUEST =
  '{"created_at":"2026-10-21T08:00:00Z","installation_id":"' +
  MOCK_INSTALLATION_ID +
  '","product_code":"platform-enterprise","tenant_id":"' +
  MOCK_TENANT_ID +
  '","version":"0.1.0"}\n';

/** `detail` License Agent trả khi tệp sai (`license-runtime/loi.properties`). */
export const CAU_LOI_MOCK_CUC_BO = {
  tepSai:
    'Tệp không đúng định dạng: cần JWS compact, tối đa 64 KB. Dùng đúng tệp nhận từ nhà cung cấp, không mở và lưu lại bằng trình soạn thảo.',
} as const;
